import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DataCardField {
  label: string;
  value: ReactNode;
}

export interface DataCardProps {
  /** Wichtigste Kennzahl, z. B. Beleg- oder Kundennummer. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rechts oben, üblicherweise ein Status-Badge. */
  badge?: ReactNode;
  /** Rechts unten hervorgehobener Wert, z. B. der Bruttobetrag. */
  metric?: ReactNode;
  /** Zusatzinformationen hinter dem Aufklapp-Pfeil. */
  details?: readonly DataCardField[];
  expandLabel: string;
  collapseLabel: string;
  onOpen?: () => void;
  actions?: ReactNode;
  /** Reserved visual entry point, such as a product image. */
  media?: ReactNode;
}

/** Kompakte Kartendarstellung eines Datensatzes für Bildschirmbreiten unterhalb von `md`. */
export function DataCard({
  title,
  subtitle,
  badge,
  metric,
  details = [],
  expandLabel,
  collapseLabel,
  onOpen,
  actions,
  media,
}: DataCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = details.length > 0;

  return (
    <li className="rounded-lg border border-border bg-card text-card-foreground">
      <div className="flex items-start gap-2 p-3">
        {media}
        <div
          className={cn('min-w-0 flex-1', onOpen === undefined ? '' : 'cursor-pointer')}
          {...(onOpen === undefined
            ? {}
            : {
                role: 'button',
                tabIndex: 0,
                onClick: onOpen,
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                  }
                },
              })}
        >
          <p className="truncate text-sm font-medium">{title}</p>
          {subtitle === undefined ? null : (
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {badge}
          {metric === undefined ? null : (
            <span className="text-sm font-semibold tabular-nums">{metric}</span>
          )}
        </div>
        {hasDetails ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? collapseLabel : expandLabel}
            className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={() => setExpanded((current) => !current)}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>

      {hasDetails && expanded ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border p-3 text-xs duration-200 animate-in fade-in-0 slide-in-from-top-1">
          {details.map((field) => (
            <div key={field.label} className="min-w-0">
              <dt className="truncate text-muted-foreground">{field.label}</dt>
              <dd className="truncate">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {actions === undefined ? null : (
        <div className="flex flex-wrap gap-2 border-t border-border p-2">{actions}</div>
      )}
    </li>
  );
}

/** Container für die Kartenansicht; blendet sich ab `md` zugunsten der Tabelle aus. */
export function DataCardList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <ul className={cn('flex flex-col gap-2 md:hidden', className)}>{children}</ul>;
}
